import { computed, nextTick, reactive, ref } from 'vue';
import { firstImageUrl, preloadImage } from './mapCardImage';

// The selection card (WHN-2179) sits at a fixed place at the bottom of the map
// instead of anchored to the marker, so it can never fall out of view. It opens
// on click only: hover-opening went out on 2026-10-02, because the pointer's
// route from a pin to the card at the bottom crosses other pins and empty map,
// and the card kept closing before it was reached.
// Clearance between the bottom of the map and the card, for the "is the clicked
// pin now under the card" check
const CARD_BOTTOM_CLEARANCE = 12;
// Before the card exists its height is unknown; this is what the wide layout
// measures, so a pin in the bottom band counts as covered either way
const CARD_HEIGHT_FALLBACK = 280;

// Shared by the projects map and the listing map. `entries` are what a marker
// or cluster selects (phases on one map, projects on the other); `items` are
// what the card browses through. By default they are the same; the listing map
// derives its items (the homes) asynchronously and passes `itemsFor`.
export const useSelectionCard = ({
  container,
  detailState,
  getMap,
  withProgrammaticMove,
  itemsFor = (entries) => entries
}) => {
  const selection = reactive({
    entries: [],
    items: [],
    index: 0,
    error: false
  });
  const hasSelection = computed(() => selection.entries.length > 0);
  const currentItem = computed(() => selection.items[selection.index] ?? null);

  const card = ref(null);
  const cardHeight = ref(0);
  const cardStyle = computed(() => ({ '--listing-map-card-height': `${cardHeight.value}px` }));

  const closeSelection = () => {
    if (!hasSelection.value) return;
    // Closing from the card's own close button would drop focus on <body>;
    // hand it to the map instead so keyboard users stay where they were.
    const focusInCard = document.activeElement?.closest('.listing-map-card');
    selection.entries = [];
    selection.items = [];
    selection.index = 0;
    selection.error = false;
    cardHeight.value = 0;
    if (focusInCard) getMap()?.getCanvas().focus({ preventScroll: true });
  };

  // Escape reaches this before the detail panel handles its own Escape (the
  // card listens in the capture phase): while the panel is open, the card
  // behind it stays, so closing the panel returns to the selected location.
  const onCardClose = () => {
    if (detailState.isOpen) return;
    closeSelection();
  };

  const sameEntries = (entries) =>
    entries.length === selection.entries.length &&
    entries.every((entry, index) => entry.id === selection.entries[index].id);

  // Clicking what is already shown keeps the card as it is, browsing position
  // included; a different selection replaces it
  const openSelection = (entries) => {
    if (sameEntries(entries)) return;
    selection.entries = entries;
    selection.items = itemsFor(entries);
    selection.index = 0;
    selection.error = false;
  };

  const step = (delta) => {
    const next = selection.index + delta;
    if (next < 0 || next >= selection.items.length) return;
    selection.index = next;
  };

  const prefetchNeighbours = (loadDetail) => {
    for (const offset of [-1, 1]) {
      const item = selection.items[selection.index + offset];
      if (!item) continue;
      loadDetail(item)
        .then((detail) => preloadImage(firstImageUrl(detail?.images)))
        .catch(() => {});
    }
  };

  const onCardResize = (height) => {
    cardHeight.value = height;
  };

  const cardElementHeight = () => card.value?.$el?.offsetHeight ?? 0;

  // Camera offset that lands the marker in the middle of the map area above
  // the card, instead of in the middle of the map where the card may cover it.
  // A one-off offset rather than map padding: padding would persist into
  // fitBounds and the radius fit.
  const selectionOffset = () => [0, -Math.round(cardElementHeight() / 2)];

  const isUnderCard = (point) =>
    point.y >
    container.value.clientHeight - (cardElementHeight() || CARD_HEIGHT_FALLBACK) - CARD_BOTTOM_CLEARANCE;

  // Camera moves caused by a click are flagged programmatic: a click shows the
  // card and must not rewrite the bbox, so the listing next to the map only
  // follows the reader's own panning and zooming (2026-10-02; until then the
  // marker click zoomed in and the listing followed, which forced the reader
  // to zoom back out for the rest of the results).
  // After the card has rendered (nextTick), so the offset knows its height.
  const centerOnMarker = async (coordinates) => {
    await nextTick();
    const map = getMap();
    if (!map) return;
    withProgrammaticMove(() => map.easeTo({ center: coordinates, offset: selectionOffset() }));
  };

  // The click on a pin only shows the card; the map pans when the card would
  // otherwise cover the clicked pin
  const revealClickedPin = async (coordinates, point) => {
    await nextTick();
    const map = getMap();
    if (!map || !isUnderCard(point)) return;
    withProgrammaticMove(() => map.easeTo({ center: coordinates, offset: selectionOffset() }));
  };

  return {
    selection,
    hasSelection,
    currentItem,
    card,
    cardStyle,
    openSelection,
    closeSelection,
    onCardClose,
    step,
    onCardResize,
    prefetchNeighbours,
    isUnderCard,
    centerOnMarker,
    revealClickedPin
  };
};
